import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { automationsService as svc } from '@/services/automations.service';
import type {
  AdminAutomation,
  AdminAutomationFilters,
  AdminAutomationUpdate,
  AdminCreateBody,
  AdminRunFilters,
  AdminSettings,
  Granularity,
} from '@/types/automations';

export const automationKeys = {
  all: ['automations'] as const,
  mine: () => ['automations', 'mine'] as const,
  mineOne: (id: string) => ['automations', 'mine', id] as const,
  myRuns: (id: string, page: number, limit: number) => ['automations', 'mine', id, 'runs', page, limit] as const,
  spending: (id: string, g: Granularity, page: number, limit: number) =>
    ['automations', 'mine', id, 'spending', g, page, limit] as const,
  hasAny: () => ['automations', 'has-any'] as const,
  admin: ['automations', 'admin'] as const,
  adminList: (f: AdminAutomationFilters) => ['automations', 'admin', 'list', f] as const,
  adminOne: (id: string) => ['automations', 'admin', 'one', id] as const,
  history: (id: string) => ['automations', 'admin', 'history', id] as const,
  runs: (f: AdminRunFilters) => ['automations', 'admin', 'runs', f] as const,
  run: (id: string) => ['automations', 'admin', 'run', id] as const,
  settings: () => ['automations', 'admin', 'settings'] as const,
  accounts: (q: string) => ['automations', 'admin', 'accounts', q] as const,
};

// ---- customer ----
export function useListMine() {
  return useQuery({ queryKey: automationKeys.mine(), queryFn: () => svc.listMine() });
}

export function useGetMine(id: string | undefined) {
  return useQuery({
    queryKey: automationKeys.mineOne(id ?? ''),
    queryFn: () => svc.getMine(id!),
    enabled: !!id,
  });
}

export function useSetEnabled(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => svc.setEnabled(id, enabled),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: automationKeys.mineOne(id) });
      void qc.invalidateQueries({ queryKey: automationKeys.mine() });
    },
  });
}

export function useListMyRuns(id: string | undefined, page = 1, limit = 20) {
  return useQuery({
    queryKey: automationKeys.myRuns(id ?? '', page, limit),
    queryFn: () => svc.listMyRuns(id!, page, limit),
    enabled: !!id,
  });
}

export function useGetSpending(id: string | undefined, granularity: Granularity, page = 1, limit = 50) {
  return useQuery({
    queryKey: automationKeys.spending(id ?? '', granularity, page, limit),
    queryFn: () => svc.getSpending(id!, granularity, page, limit),
    enabled: !!id,
    placeholderData: keepPreviousData,
  });
}

export function useExtendImages(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => svc.extendImages(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: automationKeys.mineOne(id) }),
  });
}

export function useHasAny(enabled = true) {
  return useQuery({
    queryKey: automationKeys.hasAny(),
    queryFn: () => svc.hasAny(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

// ---- admin ----
export function useSearchAccounts(q: string) {
  return useQuery({
    queryKey: automationKeys.accounts(q),
    queryFn: () => svc.searchAccounts(q),
    enabled: q.trim().length >= 2,
  });
}

export function useAdminList(filters: AdminAutomationFilters = {}) {
  return useQuery({ queryKey: automationKeys.adminList(filters), queryFn: () => svc.adminList(filters) });
}

export function useAdminGet(id: string | undefined) {
  return useQuery({
    queryKey: automationKeys.adminOne(id ?? ''),
    queryFn: () => svc.adminGet(id!),
    enabled: !!id,
  });
}

/** Write a returned automation into the cache and refresh lists. */
function useAutomationWriter() {
  const qc = useQueryClient();
  return (a: AdminAutomation) => {
    qc.setQueryData(automationKeys.adminOne(a.automation_id), a);
    void qc.invalidateQueries({ queryKey: [...automationKeys.admin, 'list'] });
  };
}

export function useAdminCreate() {
  const write = useAutomationWriter();
  return useMutation({ mutationFn: (body: AdminCreateBody) => svc.adminCreate(body), onSuccess: write });
}

export function useAdminUpdate(id: string) {
  const write = useAutomationWriter();
  return useMutation({ mutationFn: (body: AdminAutomationUpdate) => svc.adminUpdate(id, body), onSuccess: write });
}

export function useAdminDuplicate(id: string) {
  const write = useAutomationWriter();
  return useMutation({ mutationFn: (clientId: string) => svc.adminDuplicate(id, clientId), onSuccess: write });
}

export function useAdminDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => svc.adminDelete(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: automationKeys.admin }),
  });
}

export function useRegenerateToken(id: string) {
  const write = useAutomationWriter();
  return useMutation({ mutationFn: () => svc.regenerateToken(id), onSuccess: write });
}

export function useRegenerateSecret(id: string) {
  const write = useAutomationWriter();
  return useMutation({ mutationFn: () => svc.regenerateSecret(id), onSuccess: write });
}

export function useImportKlaviyoKey(id: string) {
  const write = useAutomationWriter();
  return useMutation({ mutationFn: () => svc.importKlaviyoKey(id), onSuccess: write });
}

export function useRefine(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (brief: string) => svc.refine(id, brief),
    onSuccess: () => void qc.invalidateQueries({ queryKey: automationKeys.history(id) }),
  });
}

export function useHistory(id: string | undefined) {
  return useQuery({
    queryKey: automationKeys.history(id ?? ''),
    queryFn: () => svc.history(id!),
    enabled: !!id,
  });
}

export function useTest(id: string) {
  return useMutation({
    mutationFn: (v: { row: Record<string, unknown>; automation?: AdminAutomationUpdate }) =>
      svc.test(id, v.row, v.automation),
  });
}

export function useEstimate(id: string) {
  return useMutation({
    mutationFn: (v: { runs: number; samples?: Record<string, unknown>[] }) => svc.estimate(id, v.runs, v.samples),
  });
}

export function useSendTestEvent(id: string) {
  return useMutation({
    mutationFn: (v: { row: Record<string, unknown>; profileEmail: string }) =>
      svc.sendTestEvent(id, v.row, v.profileEmail),
  });
}

export function useListRuns(filters: AdminRunFilters = {}) {
  return useQuery({ queryKey: automationKeys.runs(filters), queryFn: () => svc.listRuns(filters) });
}

export function useGetRun(runId: string | undefined) {
  return useQuery({
    queryKey: automationKeys.run(runId ?? ''),
    queryFn: () => svc.getRun(runId!),
    enabled: !!runId,
  });
}

export function useGetSettings() {
  return useQuery({ queryKey: automationKeys.settings(), queryFn: () => svc.getSettings() });
}

export function usePutSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminSettings) => svc.putSettings(body),
    onSuccess: (s) => qc.setQueryData(automationKeys.settings(), s),
  });
}
